import { Button } from "@community-project-org/ui";
import { useQuery } from "@tanstack/react-query";
import { Route as RouteIcon } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest } from "../../common/api/apiClient.js";
import { Meter } from "../../common/components/Meter.js";
import { Money } from "../../common/components/Money.js";
import { ServiceStatusPill, type ServiceStatus } from "../../common/components/BookingStatusPill.js";
import {
  ServiceTypeBadge,
  serviceTypeClass,
} from "../../common/components/ServiceTypeBadge.js";
import { useActiveMembership } from "../../common/membership/ActiveMembership.js";
import type { components } from "../../generated/api.js";
import { GroupSelect } from "../groups/GroupSelect.js";
import { ServiceGroupsPanel } from "../groups/ServiceGroupsPanel.js";
import { StopsSheet } from "../routes/StopsSheet.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type ServiceGroupDTO = components["schemas"]["ServiceGroupDTO"];

/** Der Angebotskatalog des Mandanten. Anlegen und Bearbeiten folgen als eigene Seiten. */
export function ServicesPage(): JSX.Element {
  const services = useQuery({
    queryKey: ["services"],
    queryFn: () => apiRequest<ServiceDTO[]>("/services"),
  });

  const groups = useQuery({
    queryKey: ["service-groups"],
    queryFn: () => apiRequest<ServiceGroupDTO[]>("/service-groups"),
  });
  const { can } = useActiveMembership();
  const [stopsOf, setStopsOf] = useState<ServiceDTO | null>(null);

  const list = services.data ?? [];
  const groupList = groups.data ?? [];

  return (
    <div className="px-5 pb-8">
      <h1 className="pt-5 text-[22px] font-extrabold tracking-[-0.03em] text-ink">
        Angebote
      </h1>

      {services.isPending && <p className="mt-5 text-[14px] text-ink-3">Wird geladen …</p>}

      {services.isError && (
        <p className="mt-5 text-[14px] text-ink-2">
          Für diese Rolle ist der Katalog nicht sichtbar.
        </p>
      )}

      {list.length === 0 && !services.isPending && !services.isError && (
        <p className="mt-5 text-[14px] text-ink-3">
          Noch kein Angebot angelegt. Neue Dienstleistungen starten immer als Entwurf.
        </p>
      )}

      {can("service.group.manage") && (
        <div className="mt-5">
          <ServiceGroupsPanel groups={groupList} />
        </div>
      )}

      <ul className="mt-5 flex flex-col gap-2.5">
        {list.map((service) => (
          <li
            key={service.id}
            className={`${serviceTypeClass(service.type)} card hover pad flex items-start gap-3`}
          >
            <ServiceTypeBadge type={service.type} square />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-bold tracking-[-0.015em] text-ink">
                {service.title}
              </p>
              {service.description && (
                <p className="mt-0.5 line-clamp-2 text-[13px] text-ink-2">{service.description}</p>
              )}
              {/* Zeit und Ort zuerst: in einem Spielplan sucht man die Vorstellung,
                  nicht den Datensatz. */}
              <p className="num mt-1 text-[12.5px] font-bold text-ink-2">
                {[
                  service.startsAt
                    ? new Date(service.startsAt).toLocaleString("de-DE", {
                        weekday: "short",
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : null,
                  service.venueName,
                ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <ServiceStatusPill status={service.status as ServiceStatus} />
                <span className="num text-[12.5px] font-bold text-ink-3">
                  {service.stopCount > 0
                    ? `${service.stopCount} Halte`
                    : service.seatSelection
                      ? "Platzwahl"
                      : "freie Platzwahl"}
                </span>
                {can("service.update") && service.type === "EVENT" && !service.seatSelection && (
                  <Button variant="secondary" onClick={() => setStopsOf(service)}>
                    <RouteIcon size={14} aria-hidden="true" />
                    {service.stopCount > 0 ? "Halte" : "Strecke"}
                  </Button>
                )}
                {can("service.update") && groupList.length > 0 && (
                  <GroupSelect service={service} groups={groupList} />
                )}
                {service.capacity !== null && (
                  <span className="min-w-[9rem] flex-1">
                    <Meter used={service.bookedSeats} capacity={service.capacity} thin />
                  </span>
                )}
              </div>
            </div>
            <span className="text-[15px] font-extrabold text-ink">
              <Money cents={service.priceCents} currency={service.currency} />
            </span>
          </li>
        ))}
      </ul>

      {stopsOf && <StopsSheet service={stopsOf} onClose={() => setStopsOf(null)} />}
    </div>
  );
}
